import { NumberInput } from '@mantine/core';
import TagsInput from '@xver/components/Atoms/TagsInput/TagsInput';
import { useTranslation } from 'react-i18next';

export interface ContainerConfigFormValue {
    container_port: number;
    cpu: number;
    memory: number;
    request_timeout: number;
    request_concurrency: number;
    min_replicas: number;
    max_replicas: number;
}

export interface EntrypointValue {
    entrypoint_command: string[];
    entrypoint_argument: string[];
}

export const DEFAULT_ENTRYPOINT_VALUE: EntrypointValue = {
    entrypoint_command: [],
    entrypoint_argument: [],
};

interface ContainerConfigFormProps {
    value: ContainerConfigFormValue;
    onChange: (value: ContainerConfigFormValue) => void;
    errors?: Partial<Record<keyof ContainerConfigFormValue, string>>;
    /** Locks the replica inputs. The caller renders the explanation alongside the form. */
    replicasDisabled?: boolean;
    /** Pass both to show the entrypoint overrides; omit to hide the block entirely. */
    entrypoint?: EntrypointValue;
    onEntrypointChange?: (value: EntrypointValue) => void;
}

export const DEFAULT_CONTAINER_CONFIG_VALUE: ContainerConfigFormValue = {
    container_port: 8080,
    cpu: 1000,
    memory: 512,
    request_timeout: 300,
    request_concurrency: 50,
    min_replicas: 0,
    max_replicas: 3,
};

const NUMBER_INPUT_STYLES = {
    input: {
        backgroundColor: 'var(--surface-2)',
        color: 'var(--fg)',
    },
    label: {
        color: 'color-mix(in srgb, var(--fg) 85%, transparent)',
        marginBottom: '0.25rem',
    },
    description: {
        color: 'color-mix(in srgb, var(--fg) 68%, transparent)',
    },
    error: {
        color: 'var(--color-error)',
    },
    control: {
        borderColor: 'var(--line)',
        color: 'var(--fg)',
    },
};

function toNumber(next: number | string, fallback: number): number {
    if (typeof next === 'number' && !Number.isNaN(next)) return next;
    if (typeof next === 'string') {
        const parsed = Number(next);
        if (!Number.isNaN(parsed)) return parsed;
    }
    return fallback;
}

export default function ContainerConfigForm({
    value,
    onChange,
    errors,
    replicasDisabled,
    entrypoint,
    onEntrypointChange,
}: ContainerConfigFormProps) {
    const { t } = useTranslation('containerEngine');
    const update = (patch: Partial<ContainerConfigFormValue>) => onChange({ ...value, ...patch });

    return (
        <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-4">
            <NumberInput
                label={t('deployment.containerConfig.port', 'Container port')}
                description={t(
                    'deployment.containerConfig.portDescription',
                    'Port that your container listens on.'
                )}
                min={1}
                max={65535}
                value={value.container_port}
                onChange={(n) => update({ container_port: toNumber(n, value.container_port) })}
                styles={NUMBER_INPUT_STYLES}
                error={errors?.container_port}
                allowDecimal={false}
            />
            <NumberInput
                label={t('deployment.containerConfig.cpu', 'CPU (m)')}
                description={t(
                    'deployment.containerConfig.cpuDescription',
                    'CPU allocation in millicores. 1000m = 1 vCPU.'
                )}
                min={50}
                step={50}
                value={value.cpu}
                onChange={(n) => update({ cpu: toNumber(n, value.cpu) })}
                styles={NUMBER_INPUT_STYLES}
                error={errors?.cpu}
                allowDecimal={false}
            />
            <NumberInput
                label={t('deployment.containerConfig.memory', 'Memory (MB)')}
                description={t(
                    'deployment.containerConfig.memoryDescription',
                    'Memory allocation per instance, in megabytes.'
                )}
                min={64}
                step={64}
                value={value.memory}
                onChange={(n) => update({ memory: toNumber(n, value.memory) })}
                styles={NUMBER_INPUT_STYLES}
                error={errors?.memory}
                allowDecimal={false}
            />
            <NumberInput
                label={t('deployment.containerConfig.timeout', 'Request timeout (s)')}
                description={t(
                    'deployment.containerConfig.timeoutDescription',
                    'Maximum time the service can take to handle one request.'
                )}
                min={1}
                max={3600}
                value={value.request_timeout}
                onChange={(n) => update({ request_timeout: toNumber(n, value.request_timeout) })}
                styles={NUMBER_INPUT_STYLES}
                error={errors?.request_timeout}
                allowDecimal={false}
            />
            <NumberInput
                label={t('deployment.containerConfig.concurrency', 'Request concurrency')}
                description={t(
                    'deployment.containerConfig.concurrencyDescription',
                    'Concurrent requests handled by one instance.'
                )}
                min={1}
                max={1000}
                value={value.request_concurrency}
                onChange={(n) =>
                    update({ request_concurrency: toNumber(n, value.request_concurrency) })
                }
                styles={NUMBER_INPUT_STYLES}
                error={errors?.request_concurrency}
                allowDecimal={false}
            />
            <div />
            <NumberInput
                label={t('deployment.containerConfig.minReplicas', 'Min replicas')}
                description={t(
                    'deployment.containerConfig.minReplicasDescription',
                    'Set to 0 to enable scale-to-zero.'
                )}
                min={0}
                max={100}
                value={value.min_replicas}
                onChange={(n) => update({ min_replicas: toNumber(n, value.min_replicas) })}
                styles={NUMBER_INPUT_STYLES}
                error={errors?.min_replicas}
                disabled={replicasDisabled}
                allowDecimal={false}
            />
            <NumberInput
                label={t('deployment.containerConfig.maxReplicas', 'Max replicas')}
                description={t(
                    'deployment.containerConfig.maxReplicasDescription',
                    'Upper bound for autoscaling.'
                )}
                min={1}
                max={100}
                value={value.max_replicas}
                onChange={(n) => update({ max_replicas: toNumber(n, value.max_replicas) })}
                styles={NUMBER_INPUT_STYLES}
                error={errors?.max_replicas}
                disabled={replicasDisabled}
                allowDecimal={false}
            />
            {entrypoint && onEntrypointChange && (
                <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <TagsInput
                        size="md"
                        label={t(
                            'deployment.containerConfig.entrypointCommand',
                            'Entrypoint command'
                        )}
                        value={entrypoint.entrypoint_command}
                        onChange={(next) =>
                            onEntrypointChange({ ...entrypoint, entrypoint_command: next })
                        }
                        descriptionBottom={t(
                            'deployment.containerConfig.entrypointCommandDescription',
                            'Overrides the image ENTRYPOINT. Leave empty to keep the image default.'
                        )}
                    />
                    <TagsInput
                        size="md"
                        label={t(
                            'deployment.containerConfig.entrypointArgument',
                            'Entrypoint arguments'
                        )}
                        value={entrypoint.entrypoint_argument}
                        onChange={(next) =>
                            onEntrypointChange({ ...entrypoint, entrypoint_argument: next })
                        }
                        descriptionBottom={t(
                            'deployment.containerConfig.entrypointArgumentDescription',
                            'Overrides the image CMD. Leave empty to keep the image default.'
                        )}
                    />
                </div>
            )}
        </div>
    );
}
